import { useState, useEffect, useCallback, useRef } from "react";
import { getQuote } from "../services/api";
import type {
  IQuoteResponse,
  IQuoteRequest,
  QuoteAnchor,
} from "../services/api";
import { getEvents, safeCapture } from "../analytics";
import {
  BINDING_QUOTE_DEBOUNCE_MS,
  isBindingQuoteReady,
  quoteUiOnIdentityChange,
} from "../utils/quoteMatchesSelection";
import { quoteErrorCode } from "../utils/quoteError";

// Buffer de seguridad: refresh automático N segundos antes de expiración,
// para evitar que el quote expire mientras el usuario hace click en "Confirmar".
const EXPIRATION_BUFFER_SECONDS = 5;

// Umbral de advertencia: mostrar warning cuando quedan N segundos.
const WARNING_THRESHOLD_SECONDS = 10;

interface UseQuoteResult {
  quote: IQuoteResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  isExpiringSoon: boolean;
  timeRemaining: number;
}

/**
 * Parameters for the binding quote. The quote is fetched only when a concrete
 * `capabilityKey` (the exact SKU picked from the pre-quote) and a positive
 * `amount` are available — the pricing engine requires the SKU to price + freeze
 * the quote.
 */
export interface UseQuoteParams {
  sourceCountry: string;
  destinationCountry: string;
  fundingMethod: string | null;
  payoutMethod: string | null;
  capabilityKey: string | null;
  amount: number | null;
  currency: string;
  /** Which leg `amount` is pinned to. Defaults to `send` for send-anchored links. */
  anchor?: QuoteAnchor;
  cardId?: string | null;
  /** ISO 4217 destination currency from the claim pin. Always sent. */
  destinationCurrency?: string;
}

/**
 * Hook to fetch a binding quote for a money transfer. The quote is fetched when
 * `capabilityKey`, `fundingMethod`, `payoutMethod` and a positive `amount` are
 * provided. Automatically refreshes the quote before it expires (with a buffer).
 *
 * Identity changes (method / SKU / amount) set loading and clear the previous
 * JWT **before** the debounce, so confirm cannot freeze a stale rail.
 */
export const useQuote = (params: UseQuoteParams): UseQuoteResult => {
  const {
    sourceCountry,
    destinationCountry,
    fundingMethod,
    payoutMethod,
    capabilityKey,
    amount,
    currency,
    anchor,
    cardId,
    destinationCurrency,
  } = params;

  const [quote, setQuote] = useState<IQuoteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  const isRefreshing = useRef(false);
  const fetchGen = useRef(0);

  const fetchQuote = useCallback(async (options?: { silent?: boolean }) => {
    if (
      !isBindingQuoteReady({
        capabilityKey,
        fundingMethod,
        payoutMethod,
        amount,
      })
    ) {
      setQuote(null);
      setLoading(false);
      setError(null);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
      return;
    }

    const gen = fetchGen.current;
    setLoading(true);
    setError(null);

    try {
      const request: IQuoteRequest = {
        sourceCountry,
        destinationCountry,
        fundingMethod: fundingMethod as string,
        payoutMethod: payoutMethod as string,
        capabilityKey: capabilityKey as string,
        amount: amount as number,
        currency,
        ...(anchor ? { anchor } : {}),
        ...(destinationCurrency ? { destinationCurrency } : {}),
      };

      if (cardId) {
        request.cardId = cardId;
      }

      const result = await getQuote(request, {
        raasSilentOffline: options?.silent,
      });
      if (gen !== fetchGen.current) return;

      setQuote(result);

      const expiresAt = new Date(result.expiresAt).getTime();
      const now = Date.now();
      const remainingSeconds = Math.max(
        0,
        Math.floor((expiresAt - now) / 1000)
      );

      setTimeRemaining(remainingSeconds);
      setIsExpiringSoon(remainingSeconds <= WARNING_THRESHOLD_SECONDS);

      if (!isRefreshing.current) {
        safeCapture(getEvents().QUOTE_CREATED, {
          success: true,
          quote_id: result.quoteId,
          send_amount: result.sendAmount,
          receive_amount: result.receiveAmount,
          exchange_rate: result.exchangeRate,
          total_fee: result.totalFee,
          total_cost: result.totalCost,
          expires_at: result.expiresAt,
          capability_key: capabilityKey,
          funding_method: fundingMethod,
          payout_method: payoutMethod,
          source_currency: result.sourceCurrency,
          destination_currency: result.destinationCurrency,
          estimated: false,
        });
      }

      isRefreshing.current = false;
    } catch (err) {
      if (gen !== fetchGen.current) return;
      setError(err instanceof Error ? err : new Error("Failed to fetch quote"));
      setQuote(null);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
      if (!isRefreshing.current) {
        safeCapture(getEvents().QUOTE_CREATED, {
          success: false,
          error: err instanceof Error ? err.message : "Quote failed",
          error_code: quoteErrorCode(err) ?? "QUOTE_FAILED",
          capability_key: capabilityKey,
          funding_method: fundingMethod,
          payout_method: payoutMethod,
        });
      }
      isRefreshing.current = false;
    } finally {
      if (gen === fetchGen.current) {
        setLoading(false);
      }
    }
  }, [
    sourceCountry,
    destinationCountry,
    fundingMethod,
    payoutMethod,
    capabilityKey,
    amount,
    currency,
    anchor,
    cardId,
    destinationCurrency,
  ]);

  // Invalidate synchronously when the quote identity changes, then debounce
  // the network fetch. TTL auto-refresh calls fetchQuote directly and must
  // not go through this path (it would null the still-valid JWT).
  useEffect(() => {
    const ready = isBindingQuoteReady({
      capabilityKey,
      fundingMethod,
      payoutMethod,
      amount,
    });
    const ui = quoteUiOnIdentityChange(ready);
    fetchGen.current += 1;
    if (ui.clearQuote) {
      setQuote(null);
      setError(null);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
    }
    setLoading(ui.loading);

    if (!ready) {
      return;
    }

    const timeoutId = setTimeout(() => {
      void fetchQuote();
    }, BINDING_QUOTE_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [fetchQuote, capabilityKey, fundingMethod, payoutMethod, amount]);

  // Timer para actualizar timeRemaining y auto-refresh cuando expira.
  useEffect(() => {
    if (!quote?.expiresAt) {
      return;
    }

    const intervalId = setInterval(() => {
      const expiresAt = new Date(quote.expiresAt).getTime();
      const now = Date.now();
      const remainingSeconds = Math.max(
        0,
        Math.floor((expiresAt - now) / 1000)
      );

      setTimeRemaining(remainingSeconds);
      setIsExpiringSoon(remainingSeconds <= WARNING_THRESHOLD_SECONDS);

      if (
        remainingSeconds <= EXPIRATION_BUFFER_SECONDS &&
        !isRefreshing.current
      ) {
        isRefreshing.current = true;
        fetchQuote({ silent: true });
      }

      if (remainingSeconds <= 0 && !isRefreshing.current) {
        isRefreshing.current = true;
        fetchQuote({ silent: true });
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [quote?.expiresAt, fetchQuote]);

  return {
    quote,
    loading,
    error,
    refetch: fetchQuote,
    isExpiringSoon,
    timeRemaining,
  };
};
